import React, { useState, useEffect, FC } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { Row, Col, Button, Alert } from "reactstrap";
import FormInput from "../../form-components/InputComponent";
import FormSelect from "../../form-components/SelectComponent";
import Swal from "sweetalert2";
import { Icon } from "../../../components/Component";
import taxMasterApis from "../../../api/master/taxmaster";
import { useSelector } from "react-redux";
import useSequence from "../../../hooks/useSequence";

interface SelectOptions {
  label: string;
  value: string;
}

const TaxMasterForm: FC<{
  id?: string | null;
  formAction?: string | null;
  toggleModelPopUp: () => void;
  refreshData?: () => void;
}> = ({ id, formAction, toggleModelPopUp, refreshData }) => {
  const [action, setAction] = useState(formAction);
  const [taxDetail, setTaxDetail] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const methods = useForm({
    mode: "onChange",
  });

  const { hasSequence } = useSequence(173);

  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = methods;

  const [alertInfo, setAlertInfo] = useState({
    type: "",
    message: "",
    errors: [],
  });

  const taxTypeOptions = [
    { label: "INDIRECT", value: "INDIRECT" },
    { label: "TDS", value: "TDS" },
    { label: "TCS", value: "TCS" },
    { label: "CESS", value: "CESS" },
  ];

  const fetchTaxDetails = async (taxId: any) => {
    try {
      setLoading(true);
      const response = await taxMasterApis.fetchTaxById(taxId);
      if (response && response.status) {
        const data = response.data;
        setTaxDetail(data);
        setValue("taxSequenceId", data.taxSequenceId);
        setValue("taxName", data.taxName);
        setValue("taxRate", data.taxRate);
        setValue("taxType", data.taxType);
      }
    } catch (error) {
      // console.error("Error fetching tax details:", error);
      setAlertInfo({
        type: "error",
        message: "Failed to load tax details",
        errors: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      // Validate ID if sequence is not used
      if (!hasSequence && (!data.taxSequenceId || data.taxSequenceId.trim() === "")) {
        // Using setError from react-hook-form
        methods.setError("taxSequenceId", {
          type: "required",
          message: "Tax ID is required",
        });
        setLoading(false);
        return;
      }

      // Create payload with only required fields
      const submitData: { taxSequenceId?: string; taxName: string; taxRate: number; taxType: string } = {
        taxName: data.taxName,
        taxRate: parseFloat(data.taxRate),
        taxType: data.taxType,
      };

      // console.log("Submitting data:", submitData); 
      // console.log("Action:", action, "ID:", id); 

      if (!hasSequence && action !== "edit") {
        submitData.taxSequenceId = data.taxSequenceId;
      }

      let response;

      if (action === "edit" && id) {
        const hasChanges =
          submitData.taxName !== taxDetail.taxName ||
          submitData.taxRate !== parseFloat(taxDetail.taxRate) ||
          submitData.taxType !== taxDetail.taxType;

        if (!hasChanges) {
          await Swal.fire({
            title: "No Changes",
            text: "No fields were modified.",
            icon: "info",
            confirmButtonText: "OK",
          });
          setLoading(false);
          return;
        }
        // Convert id to number for the API call
        response = await taxMasterApis.updateTaxById(Number(id), submitData);
      } else {
        response = await taxMasterApis.createTax(submitData);
      }

      // console.log("API response:", response); 

      if (response.status) {
        toggleModelPopUp();

        await Swal.fire({
          title: "Success!",
          text:
            action === "edit" ? "Tax has been updated" : "Tax has been created",
          icon: "success",
          confirmButtonText: "OK",
        });
        // toggleModelPopUp();
        if (refreshData) {
          refreshData();
        }
      } else {
        const alertMessage: string = Array.isArray(response.message)
          ? response.message.join(", ")
          : typeof response.message === "object"
            ? JSON.stringify(response.message)
            : response.message || "Operation failed";

        toggleModelPopUp();

        // setAlertInfo({
        //   type: "error",
        //   message: alertMessage,
        //   errors: [],
        // });
        await Swal.fire({
          title: "Error!",
          text: alertMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
        // toggleModelPopUp();
      }
    } catch (error: any) {
      // console.error("Error saving tax:", error);
      toggleModelPopUp();

      await Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "An error occurred while saving",
        icon: "error",
        confirmButtonText: "OK",
      });
      // toggleModelPopUp();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && action === "edit") {
      fetchTaxDetails(id);
    }
  }, [id, action, setValue]);

  if (loading) {
    return (
      <div className="text-center p-4">
        <Icon name="spinner" className="spin" /> Loading...
      </div>
    );
  }

  return (
    <>
      <h5 className="title">
        {action === "edit"
          ? `Edit ${taxDetail.taxName || "Tax"}`
          : "Create New Tax"}
      </h5>

      {alertInfo.message && (
        <div className="mb-3 mt-1">
          <Alert
            color={alertInfo.type === "error" ? "danger" : "success"}
            className="alert-icon"
          >
            <Icon
              name={
                alertInfo.type === "error" ? "alert-circle" : "check-circle"
              }
            />
            <strong>{alertInfo.message}</strong>
            {alertInfo.errors.length > 0 ? (
              <ul>
                {alertInfo.errors.map((err: any, index: any) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
            ) : (
              ""
            )}
          </Alert>
        </div>
      )}

      <div style={{ marginTop: "15px" }}></div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {!hasSequence && (
              <Col md={6}>
                <FormInput
                  name="taxSequenceId"
                  label="Tax ID"
                  required={true}
                  type="text"
                  pattern={/^[a-zA-Z0-9./_-]+$/}
                  maxLength={30}
                  disabled={action === "edit"}
                  placeholder="Enter Tax ID"
                />
              </Col>
            )}

            <Col md={6}>
              <FormSelect
                name="taxType"
                label="Tax Type"
                options={taxTypeOptions}
                required={true}
                placeholder="Select Tax Type"
              />
            </Col>

            <Col md={6}>
              <FormInput
                name="taxName"
                label="Tax Name"
                type="text"
                placeholder="Enter Tax Name"
                required={true}
                maxLength={100}
              />
            </Col>

            <Col md={6}>
              <FormInput
                name="taxRate"
                label="Tax Rate (%)"
                type="number"
                min="0"
                placeholder="Enter Tax Rate"
                required={true}
              />
            </Col>
          </Row>

          <Row className="mt-4">
            <Col lg="12">
              <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                <li>
                  <Button
                    className="btn btn-primary btn-md"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    {/* <Icon name={action === "edit" ? "edit" : "plus"} /> */}
                    <span>
                      {loading
                        ? "Processing..."
                        : action === "edit"
                          ? "Update"
                          : "Submit"}{" "}
                    </span>
                  </Button>
                </li>
                <li>
                  <Button
                    color="light"
                    outline
                    onClick={(ev) => {
                      ev.preventDefault();
                      toggleModelPopUp();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            </Col>
          </Row>
        </form>
      </FormProvider>
    </>
  );
};

export default TaxMasterForm;
